import { ProductJourneyDashboardAreaResponse } from 'features/journeys/types/ProductJourneyDashboardAreaResponse';
import styled from 'styled-components';
import { ButtonIcon, Flex, ProgressBar, Typography } from 'components';
import { JourneysDownloads } from 'features/downloads';
import useJourneys from 'features/journeys/useJourneys';
import { VideoProviderEnum } from 'features/contents';
import { useMemo } from 'react';

interface AreaDetailsCardProps {
  area: ProductJourneyDashboardAreaResponse;
  internal?: boolean;
}

const Block = styled(Flex)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray_600};

  &:last-child {
    border: 0;
  }
`;

const Wrapper = styled(Flex)`
  width: 280px;
  height: 340px;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex-wrap: nowrap;

  background: ${({ theme }) => theme.colors.dark_350};
  border: 1px solid ${({ theme }) => theme.colors.gray_600};
  border-radius: 4px;

  ${(props: any) => {
    if (props['data-internal']) {
      return `
        width: inherit;
        height: auto;
        border: 0;
      `;
    }
  }}
`;

const AreaDetailsCard = ({ area, internal = false }: AreaDetailsCardProps) => {
  const { setJourneyVideoPlayer } = useJourneys();

  const showPlayerButton = useMemo(() => {
    if (area?.videoProvider && area?.externalVideoProviderIdOrUrl) {
      if (area?.videoProvider === VideoProviderEnum.ProtectedVideo && !area?.protectedVideoOtp) {
        return false;
      }

      if (area?.videoProvider === VideoProviderEnum.Panda && !area?.protectedVideoPlaybackInfo) {
        return false;
      }

      return true;
    }

    return false;
  }, [area]);

  return (
    <Wrapper data-internal={internal}>
      {(!!area?.externalVideoProviderIdOrUrl || !!area?.videoTitle || !!area?.videoDescription) && (
        <Block flexGrow={1} flexDirection="column" alignItems="flex-start" p={3}>
          {showPlayerButton && (
            <ButtonIcon
              icon="play"
              iconWidth={26}
              width={50}
              height={50}
              borderRadius="100%"
              color="white"
              mb={3}
              onClick={() => {
                setJourneyVideoPlayer({
                  videoProvider: area?.videoProvider,
                  externalVideoProviderIdOrUrl: area?.externalVideoProviderIdOrUrl,
                  protectedVideoOtp: area?.protectedVideoOtp,
                  protectedVideoPlaybackInfo: area?.protectedVideoPlaybackInfo,
                  protectedVideoPlayerTheme: area?.protectedVideoPlayerTheme,
                });
              }}
            />
          )}

          {!!area?.videoTitle && (
            <Typography color="primary_400" fontWeight="bold" mb={2}>
              {area?.videoTitle}
            </Typography>
          )}

          {!!area?.videoDescription && (
            <Typography type="type0" color="gray_500">
              {area?.videoDescription}
            </Typography>
          )}
        </Block>
      )}

      {!!area?.journeyAreaDownloads?.length && (
        <Block flexGrow={0} p={3}>
          <JourneysDownloads downloads={area?.journeyAreaDownloads} />
        </Block>
      )}

      <Block flexDirection="column" justifyContent="flex-end" flexGrow={0} p={3}>
        <Typography type="type1" color="gray_500" mb={2} upper>
          Evolução no período
        </Typography>

        <Flex alignItems="center">
          <ProgressBar percent={area?.completionPercentage || 0} bgTrack="dark_150" />

          <Flex width={40} flexGrow={0} justifyContent="flex-end">
            <Typography type="type1" color="primary_400" fontWeight="bold">
              {area?.completionPercentage || 0}%
            </Typography>
          </Flex>
        </Flex>
      </Block>
    </Wrapper>
  );
};

export default AreaDetailsCard;
